/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

export const HOTEL_DETAILS_LEGACY_ROUTE_PREFIXES = [
  '/hotels/details',
  '/details',
  '/hotel',
] as const;

/**
 * Hosted Move web mount prefixes that should be stripped before route detection.
 * - `/hotel` — current airasia.com reverse-proxy / EXPO_BASE_URL mount
 * - `/v2/hotels/search` — previous production mount (backward compatible)
 *
 * Strip repeatedly so cascaded mounts (`/hotel/hotel/...`) collapse to an
 * app-relative path before Next.js (or history helpers) re-apply a single mount.
 */
const MOVE_HOTEL_WEB_MOUNT_PREFIXES = ['/v2/hotels/search', '/hotel'] as const;

const REVISION_PREVIEW_MOUNT_RE = /^(\/hotel\/revision-preview-\d+)(?=\/|$)/;

export const normalizeWebHotelRoutePath = (path: string): string => {
  const normalizedPath = path.trim();

  if (!normalizedPath) {
    return normalizedPath;
  }

  let nextPath = normalizedPath;
  let stripped = true;

  while (stripped) {
    stripped = false;

    const revisionMatch = nextPath.match(REVISION_PREVIEW_MOUNT_RE);
    if (revisionMatch) {
      nextPath = nextPath.slice(revisionMatch[1].length) || '/';
      stripped = true;
      continue;
    }

    for (const prefix of MOVE_HOTEL_WEB_MOUNT_PREFIXES) {
      const escapedPrefix = prefix.replace(/\//g, '\\/');
      const mountRe = new RegExp(`^${escapedPrefix}(?=/|$)`);
      if (mountRe.test(nextPath)) {
        nextPath = nextPath.replace(mountRe, '') || '/';
        stripped = true;
        break;
      }
    }
  }

  return nextPath;
};

/**
 * Ensure a browser URL path has exactly one hotels-web mount prefix.
 * Use for `history.pushState` / `replaceState` (not Next.js router).
 */
export const withHotelWebMount = (
  pathAndQuery: string,
  mount = '/hotel',
): string => {
  if (/^https?:\/\//i.test(pathAndQuery)) {
    try {
      const parsed = new URL(pathAndQuery);
      const remounted = withHotelWebMount(
        `${parsed.pathname}${parsed.search}${parsed.hash}`,
        mount,
      );
      return `${parsed.origin}${remounted}`;
    } catch {
      return pathAndQuery;
    }
  }

  const hashIndex = pathAndQuery.indexOf('#');
  const beforeHash =
    hashIndex >= 0 ? pathAndQuery.slice(0, hashIndex) : pathAndQuery;
  const hashPart = hashIndex >= 0 ? pathAndQuery.slice(hashIndex) : '';
  const queryIndex = beforeHash.indexOf('?');
  const pathPart =
    queryIndex >= 0 ? beforeHash.slice(0, queryIndex) : beforeHash;
  const queryPart = queryIndex >= 0 ? beforeHash.slice(queryIndex) : '';

  let activeMount = mount.replace(/\/+$/, '') || '/hotel';
  if (typeof window !== 'undefined' && window.location?.pathname) {
    const windowMount = window.location.pathname.match(
      /^(\/hotel(?:\/revision-preview-\d+)?)(?=\/|$)/,
    );
    if (windowMount) {
      activeMount = windowMount[1];
    } else if (pathPart === window.location.pathname) {
      return pathAndQuery;
    }
  }

  const normalized = normalizeWebHotelRoutePath(pathPart);
  const mounted =
    normalized === '/'
      ? activeMount
      : `${activeMount}${normalized.startsWith('/') ? normalized : `/${normalized}`}`;

  return `${mounted}${queryPart}${hashPart}`;
};

export const HOTEL_DETAILS_RESERVED_ROOT_SEGMENTS = new Set([
  'confirmation',
  'booking-detail',
  'booking-details',
  'details',
  'hotel',
  'listing',
  'mapview',
  'payment',
  'results',
  'result',
  'search',
  'trip-details',
  'v2',
]);

export const joinHotelDetailsPath = (
  detailsPrefix: string,
  slug: string,
): string => {
  const normalizedSlug = slug.replace(/^\/+|\/+$/g, '');
  const prefix = detailsPrefix.replace(/\/+$/, '');

  if (!normalizedSlug) {
    return prefix ? `${prefix}/` : '/';
  }

  if (!prefix) {
    return `/${normalizedSlug}`;
  }

  return `${prefix}/${normalizedSlug}`;
};

export const isLegacyHotelDetailsRoutePath = (path: string): boolean =>
  path === '/details' || path.startsWith('/details/');

export const isRootLevelHotelDetailsRoutePath = (path: string): boolean => {
  const normalizedPath = path.replace(/\/+$/, '') || '/';

  if (normalizedPath === '/') {
    return false;
  }

  const segments = normalizedPath.replace(/^\//, '').split('/').filter(Boolean);

  if (segments.length !== 1 && segments.length !== 3) {
    return false;
  }

  return !HOTEL_DETAILS_RESERVED_ROOT_SEGMENTS.has(segments[0].toLowerCase());
};

export const isCanonicalHotelDetailsRoutePath = (path: string): boolean =>
  isLegacyHotelDetailsRoutePath(path) ||
  isRootLevelHotelDetailsRoutePath(normalizeWebHotelRoutePath(path));

export const isHotelDetailsNavigationUrl = (
  url: string,
  detailsPrefix: string,
): boolean => {
  const pathname = (url.split('?')[0] || '').trim();

  if (!pathname) {
    return false;
  }

  const normalizedPrefix = detailsPrefix.replace(/\/+$/, '');

  if (!normalizedPrefix || normalizedPrefix === '/') {
    return isCanonicalHotelDetailsRoutePath(pathname);
  }

  return (
    pathname === normalizedPrefix || pathname.startsWith(`${normalizedPrefix}/`)
  );
};
